import { useState, useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, Maximize2 } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { toast } from "sonner";
import { LoadCreationSidebar } from "@/components/loads/LoadCreationSidebar";
import { 
  useLoadCreationMap, 
  InstallationMarker, 
  OptimizeResult 
} from "@/hooks/useLoadCreationMap";
import type { Json } from "@/integrations/supabase/types";
import type { OptimizedWaypoint } from "@/types/supabase-helpers";

export default function LoadsCreate() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loadDate, setLoadDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [installerId, setInstallerId] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizeResult, setOptimizeResult] = useState<OptimizeResult | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("ready_for_installation");
  const [taskTypeFilter, setTaskTypeFilter] = useState<string>("all");

  // Fetch available installations based on status filter
  const { data: installations, isLoading } = useQuery({
    queryKey: ["available-installations", statusFilter],
    queryFn: async () => {
      let query = supabase
        .from("installations")
        .select(`
          id,
          order_id,
          task_type,
          order:orders!inner(
            id,
            order_number,
            status,
            order_type,
            customer:customers(first_name, last_name, company_name)
          ),
          cemetery:cemeteries!inner(id, name, city, latitude, longitude)
        `)
        .is("load_id", null)
        .is("deleted_at", null)
        .neq("status", "completed");

      if (statusFilter === "all_active") {
        query = query.in("orders.status", ["confirmed", "ready_for_installation"] as const);
      } else {
        query = query.eq("orders.status", statusFilter as "confirmed" | "ready_for_installation");
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  // Fetch installers
  const { data: installers } = useQuery({
    queryKey: ["installers"],
    queryFn: async () => {
      // First get user_ids with installer role
      const { data: roles, error: rolesError } = await supabase
        .from("user_roles")
        .select("user_id")
        .eq("role", "installer");

      if (rolesError) throw rolesError;
      if (!roles || roles.length === 0) return [];

      // Then get profiles for those users
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, first_name, last_name")
        .in("id", roles.map(r => r.user_id));

      if (profilesError) throw profilesError;
      return profiles || [];
    },
  });

  // Create markers data with task type filtering
  const markers: InstallationMarker[] = (installations || [])
    .filter(inst => inst.cemetery?.latitude && inst.cemetery?.longitude)
    .filter(inst => {
      if (taskTypeFilter === "all") return true;
      if (taskTypeFilter === "pickup") return inst.task_type === "pickup";
      if (taskTypeFilter === "return") 
        return inst.task_type === "installation" && inst.order?.order_type === "pickup_work";
      if (taskTypeFilter === "new_installation") 
        return inst.task_type === "installation" && inst.order?.order_type === "new_work";
      return true;
    })
    .map(inst => {
      const customer = inst.order?.customer;
      const customerName = customer?.company_name || 
        `${customer?.first_name || ""} ${customer?.last_name || ""}`.trim() || 
        "Tuntematon";

      return {
        id: inst.id,
        orderId: inst.order_id,
        orderNumber: inst.order?.order_number || "",
        customerName,
        cemeteryName: inst.cemetery?.name || "",
        cemeteryCity: inst.cemetery?.city || "",
        taskType: inst.task_type,
        orderType: inst.order?.order_type || "",
        orderStatus: inst.order?.status || "",
        lat: Number(inst.cemetery.latitude),
        lng: Number(inst.cemetery.longitude),
      };
    });

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    // Clear optimization when selection changes
    setOptimizeResult(null);
  }, []);

  // Use the map hook
  const { fitBounds } = useLoadCreationMap({
    containerRef: mapContainerRef,
    markers,
    selectedIds,
    onMarkerClick: toggleSelection,
    optimizeResult,
  });

  const selectedMarkers = markers.filter(m => selectedIds.has(m.id));

  const handleOptimize = async () => {
    if (selectedMarkers.length < 1) {
      toast.error("Valitse vähintään 1 kohde reitin laskentaan");
      return;
    }

    setIsOptimizing(true);
    try {
      const waypoints = selectedMarkers.map(m => ({
        id: m.id,
        lat: m.lat,
        lng: m.lng,
        name: `${m.orderNumber} - ${m.cemeteryName}`,
      }));

      const { data, error } = await supabase.functions.invoke("optimize-route", {
        body: { waypoints },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      setOptimizeResult(data);
      toast.success(selectedMarkers.length === 1 ? "Reitti laskettu" : "Reitti optimoitu!");
    } catch (error) {
      console.error("Error optimizing route:", error);
      const message = error instanceof Error ? error.message : "Tuntematon virhe";
      toast.error("Reitin optimointi epäonnistui: " + message);
    } finally {
      setIsOptimizing(false);
    }
  };

  const createLoadMutation = useMutation({
    mutationFn: async () => {
      if (!installerId) throw new Error("Valitse asentaja");
      if (selectedIds.size === 0) throw new Error("Valitse vähintään yksi kohde");

      // Create the load
      const { data: load, error: loadError } = await supabase
        .from("installation_loads")
        .insert([{
          load_date: loadDate,
          installer_id: installerId,
          notes: notes || null,
          total_distance_km: optimizeResult?.totalDistanceKm || null,
          estimated_duration_min: optimizeResult?.estimatedDurationMin || null,
          route_geometry: (optimizeResult?.routeGeometry as unknown ?? null) as Json,
          optimized_order: (optimizeResult?.optimizedOrder as unknown ?? null) as Json,
        }])
        .select()
        .single();

      if (loadError) throw loadError;

      // Build waypoints with proper typing
      const waypoints: OptimizedWaypoint[] = optimizeResult?.optimizedOrder || 
        selectedMarkers.map((m, i) => ({ waypointId: m.id, position: i + 1 }));

      // Update installations with load_id and position
      const updatePromises = waypoints.map((wp, index) =>
        supabase
          .from("installations")
          .update({
            load_id: load.id,
            load_position: wp.position || index + 1,
            installer_id: installerId,
            scheduled_date: loadDate,
          })
          .eq("id", wp.waypointId)
      );

      await Promise.all(updatePromises);

      return load;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["installation-loads"] });
      queryClient.invalidateQueries({ queryKey: ["available-installations"] });
      toast.success("Kuorma luotu onnistuneesti!");
      navigate("/loads");
    },
    onError: (error: Error) => {
      console.error("Error creating load:", error);
      toast.error("Kuorman luonti epäonnistui: " + error.message);
    },
  });

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setOptimizeResult(null);
  }, []);

  return (
    <Layout>
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Link to="/loads">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Takaisin
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">Luo uusi kuorma</h1>
            <p className="text-sm text-muted-foreground">
              Valitse kohteet kartalta ja optimoi reitti
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-200px)]">
          {/* Map */}
          <div className="lg:col-span-2 relative rounded-lg overflow-hidden border">
            {isLoading && (
              <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-10">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            )}
            <div ref={mapContainerRef} className="h-full w-full isolate" />
            
            {/* Map controls */}
            <div className="absolute top-4 right-4 z-10">
              <Button
                variant="secondary"
                size="sm"
                onClick={fitBounds}
              >
                <Maximize2 className="h-4 w-4 mr-1" />
                Näytä kaikki
              </Button>
            </div>
            
            {/* Legend */}
            <div className="absolute bottom-4 left-4 bg-background/95 rounded-lg p-3 shadow-lg text-sm">
              <div className="font-medium mb-2">Selite</div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-violet-600" />
                  <span>🏠 Verstas (lähtö/paluu)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-green-500" />
                  <span>Asennus (uusi työ)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-teal-500" />
                  <span>↻ Palautus (pajalta)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-orange-500" />
                  <span>↑ Nouto (pajalle)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-amber-500" />
                  <span>Ei vielä valmis</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-blue-500" />
                  <span>Optimoitu reitti</span>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <LoadCreationSidebar
            loadDate={loadDate}
            setLoadDate={setLoadDate}
            installerId={installerId}
            setInstallerId={setInstallerId}
            notes={notes}
            setNotes={setNotes}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            taskTypeFilter={taskTypeFilter}
            setTaskTypeFilter={setTaskTypeFilter}
            selectedIds={selectedIds}
            markers={markers}
            selectedMarkers={selectedMarkers}
            optimizeResult={optimizeResult}
            setOptimizeResult={setOptimizeResult}
            installers={installers}
            onOptimize={handleOptimize}
            onCreateLoad={() => createLoadMutation.mutate()}
            onClearSelection={handleClearSelection}
            onRemoveItem={toggleSelection}
            isOptimizing={isOptimizing}
            isCreating={createLoadMutation.isPending}
          />
        </div>
      </div>
    </Layout>
  );
}
